import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Package, FileSpreadsheet, Settings as SettingsIcon, LogOut, Zap, TrendingUp, ShoppingCart, ClipboardList, DollarSign, Tag, BarChart3, Users, Menu, X, Boxes, ArrowLeftRight, CreditCard, Lock, ShieldCheck, ShoppingBag, ScanLine, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";

const ALL = ["owner", "admin", "employee"];
const STAFF = ["owner", "admin"];
const nav = [
  { to: "/inventory", label: "Inventory", icon: Package, end: true, roles: ALL },
  { to: "/sell", label: "Selling", icon: DollarSign, roles: ALL },
  { to: "/trade", label: "Trade", icon: ArrowLeftRight, roles: STAFF },
  { to: "/buying", label: "Buying", icon: ShoppingCart, roles: STAFF },
  { to: "/staging", label: "Staging", icon: ClipboardList, roles: STAFF },
  { to: "/reprice", label: "Repricing", icon: TrendingUp, roles: STAFF },
  { to: "/scan", label: "Scan", icon: ScanLine, roles: STAFF },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ALL },
  { to: "/shows", label: "Shows", icon: CalendarDays, roles: STAFF },
  { to: "/boxed", label: "Boxed.gg", icon: Boxes, roles: STAFF, requires: "boxed" },
  { to: "/csv", label: "TCGPlayer CSV", icon: FileSpreadsheet, roles: STAFF, requires: "csv" },
  { to: "/labels", label: "Labels", icon: Tag, roles: ALL },
  { to: "/sync", label: "Shopify", icon: ShoppingBag, roles: STAFF, requires: "shopify" },
  { to: "/team", label: "Team", icon: Users, roles: STAFF },
  { to: "/billing", label: "Billing", icon: CreditCard, roles: ["owner"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: STAFF },
];

const Logo = () => (
  <div className="flex items-center gap-3">
    <div className="h-9 w-9 rounded-lg flex items-center justify-center holo-shine border" style={{ borderColor: "var(--pk-border)" }}>
      <Zap className="h-5 w-5 text-amber-400" />
    </div>
    <div className="font-heading font-extrabold text-lg text-slate-100 leading-tight">TCG Vendor Hub</div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { sub } = useSubscription();
  const role = user?.role || "owner";
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const ent = {
    csv: !!sub?.limits?.csv,
    boxed: !!sub?.addons?.boxed,
    shopify: !!sub?.addons?.shopify,
  };

  const items = nav.filter((n) => n.roles.includes(role));

  const navLinks = (compact = false) =>
    items.map((item) => {
      const locked = item.requires && !ent[item.requires];
      const slug = (item.label.toLowerCase().match(/[a-z0-9]+/) || ["x"])[0];
      const testid = `nav-${slug}${compact ? "-mobile" : ""}`;
      const base = `flex items-center gap-3 rounded-lg font-medium transition-colors ${compact ? "px-3 py-3 text-sm" : "px-3 py-2.5 text-sm"}`;
      if (locked) {
        return (
          <Link key={item.to} to="/billing" data-testid={`${testid}-locked`}
            className={`${base} text-slate-500 hover:text-amber-400 hover:bg-[var(--pk-hover)] border border-transparent`}
            title="Upgrade to unlock">
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            <Lock className="h-3.5 w-3.5" />
          </Link>
        );
      }
      return (
        <NavLink key={item.to} to={item.to} end={item.end} data-testid={testid}
          className={({ isActive }) =>
            `${base} ${isActive
              ? "bg-amber-500/10 text-amber-400 border border-amber-500/30"
              : "text-slate-400 hover:text-slate-100 hover:bg-[var(--pk-hover)] border border-transparent"}`
          }>
          <item.icon className="h-4 w-4 shrink-0" />
          {item.label}
        </NavLink>
      );
    });

  const userBlock = (
    <div className="space-y-2">
      {sub?.is_superadmin && (
        <Link to="/admin" data-testid="nav-admin"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-amber-400 hover:bg-[var(--pk-hover)] transition-colors">
          <ShieldCheck className="h-4 w-4" /> Admin panel
        </Link>
      )}
      <div className="flex items-center gap-3 px-2 py-2">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-semibold">
            {(user?.name || user?.email || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm text-slate-200 truncate">{user?.name || "Owner"}</div>
          <div className="text-[11px] text-slate-500 truncate">{sub?.plan_name ? `${sub.plan_name} plan` : user?.email}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--pk-bg)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 border-r h-screen sticky top-0"
        style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="px-6 py-6 border-b" style={{ borderColor: "var(--pk-border)" }}>
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">{navLinks()}</nav>
        <div className="p-3 border-t" style={{ borderColor: "var(--pk-border)" }}>
          {userBlock}
          <button data-testid="logout-button" onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b sticky top-0 z-30"
          style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-button"
            className="h-10 w-10 -ml-2 flex items-center justify-center rounded-lg text-slate-300 hover:bg-[var(--pk-hover)]">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-400" />
            <span className="font-heading font-bold text-slate-100">TCG Vendor Hub</span>
          </div>
          <div className="h-8 w-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-semibold">
            {(user?.name || user?.email || "?")[0]?.toUpperCase()}
          </div>
        </div>

        {/* Mobile slide-in drawer */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-nav-drawer">
            <div className="absolute inset-0 bg-black/60 animate-fade-up" style={{ animationDuration: "0.15s" }}
              onClick={() => setDrawerOpen(false)} data-testid="mobile-nav-overlay" />
            <aside className="absolute left-0 top-0 bottom-0 w-[82%] max-w-xs flex flex-col border-r drawer-slide-in"
              style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
              <div className="px-5 py-5 flex items-center justify-between border-b" style={{ borderColor: "var(--pk-border)" }}>
                <Logo />
                <button onClick={() => setDrawerOpen(false)} data-testid="mobile-nav-close"
                  className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-400 hover:bg-[var(--pk-hover)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">{navLinks(true)}</nav>
              <div className="p-3 border-t" style={{ borderColor: "var(--pk-border)" }}>
                {userBlock}
                <button data-testid="mobile-logout-button" onClick={handleLogout}
                  className="mt-2 w-full flex items-center gap-2 px-3 py-3 rounded-lg text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </aside>
          </div>
        )}

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
